import React from 'react';

export interface WeighingStep {
  id: number;
  title: string;
  description?: string;
  status: 'pending' | 'active' | 'completed' | 'skipped';
}

interface WeighingStepperProps {
  steps: WeighingStep[];
  currentStep: number;
  onStepClick?: (stepId: number) => void;
  className?: string;
}

const WeighingStepper: React.FC<WeighingStepperProps> = ({
  steps,
  currentStep,
  onStepClick,
  className = ''
}) => {
  const getStepIcon = (step: WeighingStep) => {
    if (step.status === 'completed') {
      return (
        <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
      );
    }
    
    if (step.status === 'active') {
      return (
        <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
          <span className="text-white font-medium text-sm">{step.id}</span>
        </div>
      );
    }
    
    if (step.status === 'skipped') {
      return (
        <div className="w-8 h-8 bg-gray-400 rounded-full flex items-center justify-center">
          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </div>
      );
    }
    
    return (
      <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center">
        <span className="text-gray-600 font-medium text-sm">{step.id}</span>
      </div>
    );
  };

  const getStepClasses = (step: WeighingStep) => {
    if (step.status === 'completed') {
      return 'text-green-600';
    }
    
    if (step.status === 'active') {
      return 'text-blue-600';
    }
    
    if (step.status === 'skipped') {
      return 'text-gray-400';
    }
    
    return 'text-gray-500';
  };

  return (
    <div className={`bg-white rounded-lg shadow-sm p-2 mb-2 ${className}`}>
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-sm font-medium text-gray-900">
          Nouvelle pesée - Étape {currentStep}/4
        </h2>
        <span className={`px-1.5 py-0.5 rounded-full text-xs font-medium ${
          currentStep === 1 ? 'bg-green-100 text-green-800' :
          currentStep === 2 ? 'bg-blue-100 text-blue-800' :
          currentStep === 3 ? 'bg-purple-100 text-purple-800' :
          'bg-orange-100 text-orange-800'
        }`}>
          Étape {currentStep}/4
        </span>
      </div>
      
      <div className="flex items-center justify-between">
        {steps.map((step, index) => (
          <React.Fragment key={step.id}>
            <div className="flex flex-col items-center">
              <button
                onClick={() => onStepClick && onStepClick(step.id)}
                className={`flex flex-col items-center ${onStepClick ? 'cursor-pointer hover:opacity-80' : 'cursor-default'}`}
                disabled={!onStepClick || step.status === 'pending'}
              >
                {getStepIcon(step)}
                <div className="mt-0.5 text-center">
                  <p className={`font-medium text-xs ${getStepClasses(step)}`}>
                    {step.title}
                  </p>
                  {step.description && (
                    <p className={`text-xs mt-0 ${getStepClasses(step)}`}>
                      {step.description}
                    </p>
                  )}
                </div>
              </button>
            </div>
            
            {index < steps.length - 1 && (
              <div className={`flex-1 h-0.5 mx-1 ${
                step.status === 'completed' ? 'bg-green-300' : 'bg-gray-300'
              }`} />
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

export default WeighingStepper;
